'use client';

import React from 'react';

import { Box, BoxProps } from './box';
import { Accordion } from './accordion';

export interface FAQItem {
  id?: string;
  question: string;
  answer: React.ReactNode;
  disabled?: boolean;
}

export interface FAQAccordionProps extends BoxProps {
  faqs: FAQItem[];
  defaultOpenItems?: string[];
  multiple?: boolean;
  variant?: 'default' | 'bordered' | 'filled' | 'separated';
  size?: 'sm' | 'md' | 'lg';
  iconType?: 'caret' | 'caret-right' | 'plus' | 'arrow';
  iconPosition?: 'left' | 'right';
  searchable?: boolean;
  searchPlaceholder?: string;
}

/**
 * FAQAccordion component for displaying frequently asked questions
 * Built on top of the Accordion system with FAQ-specific styling and behavior
 */
export const FAQAccordion: React.FC<FAQAccordionProps> = ({
  faqs,
  defaultOpenItems = [],
  multiple = true,
  variant = 'bordered',
  size = 'md',
  iconType = 'caret',
  iconPosition = 'right',
  searchable = false,
  searchPlaceholder = 'Search FAQs...',
  ...props
}) => {
  const [searchTerm, setSearchTerm] = React.useState('');

  // Generate IDs for FAQ items if not provided
  const faqsWithIds = faqs.map((faq, index) => ({
    ...faq,
    id: faq.id || `faq-${index + 1}`,
  }));

  // Filter FAQs based on search term
  const filteredFaqs =
    searchable && searchTerm
      ? faqsWithIds.filter(
          (faq) =>
            faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (typeof faq.answer === 'string' &&
              faq.answer.toLowerCase().includes(searchTerm.toLowerCase()))
        )
      : faqsWithIds;

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  return (
    <Box {...props}>
      {searchable && (
        <Box marginBottom={4}>
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={handleSearch}
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid #E2E8F0',
              borderRadius: '6px',
              fontSize: '14px',
            }}
          />
        </Box>
      )}

      {filteredFaqs.length === 0 && searchable && searchTerm ? (
        <Box textAlign="center" padding={8} color="#718096" fontSize="sm">
          No FAQs found matching "{searchTerm}"
        </Box>
      ) : (
        <Accordion.Root
          defaultValue={defaultOpenItems}
          multiple={multiple}
          variant={variant}
          size={size}
          iconType={iconType}
          iconPosition={iconPosition}
        >
          {filteredFaqs.map((faq) => (
            <Accordion.Item key={faq.id} value={faq.id!} disabled={faq.disabled}>
              <Accordion.ItemTrigger>{faq.question}</Accordion.ItemTrigger>
              <Accordion.ItemContent>
                <Accordion.ItemBody>{faq.answer}</Accordion.ItemBody>
              </Accordion.ItemContent>
            </Accordion.Item>
          ))}
        </Accordion.Root>
      )}
    </Box>
  );
};

export default FAQAccordion;
